export type SupplierBillStatus =
  | 'DRAFT' | 'UNPAID' | 'PARTIALLY_PAID' | 'PAID' | 'BLOCKED' | 'REVERSED';

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

// Single source of truth for status chip colors across the bill list and detail views.
export const SUPPLIER_BILL_STATUS_COLORS: Record<SupplierBillStatus, ChipColor> = {
  DRAFT: 'default',
  UNPAID: 'warning',
  PARTIALLY_PAID: 'info',
  PAID: 'success',
  BLOCKED: 'error',
  REVERSED: 'error',
};

// ---- FW-3B: an approval workflow holds a posted bill (BLOCKED) so it cannot be paid until it is approved ----
export type BillApprovalStatus = 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED';

export const APPROVAL_HOLD_TIP =
  'An approval workflow holds this bill. It is posted, but it cannot be paid (payment form, payment runs, cheques, bank rules) until it is approved in the Approvals inbox.';

/** BLOCKED because of an approval (waiting, or rejected), as opposed to a three-way-match variance. */
export const isOnApprovalHold = (b: { status: SupplierBillStatus; approvalStatus?: BillApprovalStatus }): boolean =>
  b.status === 'BLOCKED' && (b.approvalStatus === 'PENDING' || b.approvalStatus === 'REJECTED');

export const approvalHoldLabel = (b: { approvalStatus?: BillApprovalStatus }): string =>
  b.approvalStatus === 'REJECTED' ? 'On approval hold: rejected' : 'On approval hold: awaiting approval';
// ---- end FW-3B ----

export interface SupplierBillLine {
  id: string;
  supplierBillId: string;
  goodsReceiptLineId: string;
  purchaseOrderLineId: string;
  itemId: string;
  itemName?: string;
  itemCode?: string;
  quantity: number;
  unitPrice: number;
  poUnitPrice?: number;
  lineTotal: number;
  taxCodeId?: string | null;
  lineTax?: number;
  /** G5a: foreign PO bill: the supplier and PO unit prices in the bill currency. */
  foreignUnitPrice?: number;
  foreignPoUnitPrice?: number;
}

export interface SupplierBillLineCreateDTO {
  goodsReceiptLineId: string;
  quantity: number;
  unitPrice: number;
  taxCodeId?: string | null;
}

export interface BillReleaseBlock {
  justification: string;
}

export interface SupplierBill {
  id: string;
  billNumber: string;
  supplierInvoiceRef?: string;
  purchaseOrderId?: string;
  /** Brought-forward opening bill: no PO or GRN. */
  openingBalance?: boolean;
  purchaseOrderNumber?: string;
  supplierId: string;
  supplierName?: string;
  billDate: string;
  dueDate: string;
  status: SupplierBillStatus;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  paidAmount: number;
  /** W3-E: total of the posted debit notes against this bill. */
  debitedAmount?: number;
  // ---- B4-C: document currency (service bills; PO bills since G5a) ----
  currencyCode?: string;
  exchangeRate?: number;
  foreignSubtotal?: number;
  foreignTotal?: number;
  foreignPaid?: number;
  foreignDebited?: number;
  foreignOutstanding?: number;
  /** G5a: realised FX of a foreign PO bill between the GRN and the bill rate: gain (+) / loss (-). */
  grnFxDifference?: number;
  warnings?: string[];
  // ---- end B4-C ----
  journalEntryId?: string;
  reversalJournalEntryId?: string;
  reversedById?: string;
  reversedDate?: string;
  blockReason?: string;
  approvalStatus?: BillApprovalStatus;   // FW-3B
  approvalRequestId?: string;            // FW-3B
  releaseJustification?: string;
  releasedById?: string;
  releasedDate?: string;
  editedById?: string;
  editedDate?: string;
  editCount?: number;
  institutionId: string;
  lines: SupplierBillLine[];
}

export interface SupplierBillCreateDTO {
  purchaseOrderId: string;
  supplierInvoiceRef?: string;
  billDate: string;
  dueDate: string;
  taxAmount?: number;
  institutionId: string;
  lines: SupplierBillLineCreateDTO[];
  /** G5a: on a foreign PO the line unit prices are in the PO currency; the rate defaults to the GRN rate. */
  currencyCode?: string;
  exchangeRate?: number;
}

export interface SupplierBillResponse {
  content: SupplierBill[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface BillEditLineDTO {
  goodsReceiptLineId: string;
  unitPrice: number;
  taxCodeId?: string | null;
}

export interface BillEditDTO {
  billDate: string;
  dueDate: string;
  supplierInvoiceRef?: string;
  editReason: string;
  taxAmount?: number;
  lines: BillEditLineDTO[];
  /** G5a: the AP rate of a foreign PO bill (absent keeps the stored rate). */
  exchangeRate?: number;
}

export interface SupplierBillEditLine {
  goodsReceiptLineId: string;
  prevUnitPrice: number;
  prevLineTotal: number;
}

export interface SupplierBillEdit {
  id: string;
  supplierBillId: string;
  editSeq: number;
  editedById: string;
  editedAt: string;
  editReason: string;
  reversalJournalEntryId?: string;
  newJournalEntryId?: string;
  prevBillDate: string;
  prevDueDate: string;
  prevSupplierInvoiceRef?: string;
  prevSubtotal: number;
  prevTaxAmount: number;
  prevTotalAmount: number;
  prevStatus: string;
  prevLines: SupplierBillEditLine[];
  prevExchangeRate?: number | null;
  prevGrnFxDifference?: number | null;
}

export interface SupplierServiceBillLineDTO {
  accountId: string;
  segmentId?: string | null;
  description?: string;
  amount: number;
  taxCodeId?: string | null;
}

export interface SupplierServiceBillCreateDTO {
  institutionId: string;
  supplierId: string;
  supplierInvoiceRef?: string;
  billDate: string;
  dueDate: string;
  /** B4-C: omit (or BDT) for a local bill; line amounts are then in this currency at `exchangeRate`. */
  currencyCode?: string;
  exchangeRate?: number;
  lines: SupplierServiceBillLineDTO[];
}
